import { ability } from "@/kibi/core/plugins/casl/ability";
import { getDeviceId } from "@/kibi/core/utils/device";
import { formatPermissions } from "@/kibi/core/utils/formatPermissions";
import * as Sentry from "@sentry/vue";
import { defineStore } from "pinia";
import { computed, inject, ref } from "vue";
import { authDataSourceKey } from "../../data/datasources/auth_datasource";
import type {
  LoginRequestDTO,
  UpdatePasswordRequestDTO,
  ValidateOTPRequestDTO,
  ValidateRecoveryCodeRequestDTO,
  VerifyOTPRequestDTO,
} from "../../data/models/auth_model";
import type { AuthToken, User } from "../entities/auth";

export const useAuthStore = defineStore(
  "auth",
  () => {
    const dataSource = inject(authDataSourceKey);
    if (!dataSource) throw new Error("AuthDataSource not found");
    const user = ref<User | null>(null);
    const token = ref<AuthToken | null>(null);
    const fullUserPhoto = computed(() => {
      return import.meta.env.VITE_API_ASSET_PATH + "/" + user.value?.photo;
    });
    const userInitials = computed(() => {
      if (!user.value) return "";
      const firstInitial = user.value.first_name?.[0] || "";
      const lastInitial = user.value.last_name?.[0] || "";

      return (firstInitial + lastInitial).toUpperCase();
    });

    const setSentryUser = (u: User | null) => {
      if (u) {
        Sentry.setUser({
          id: u.id,
          username: `${u.first_name} ${u.last_name}`,
        });
      } else {
        Sentry.setUser(null);
      }
    };

    const login = async (request: LoginRequestDTO) => {
      const response = await dataSource.login(request);

      user.value = response.user;
      token.value = response.token;

      setSentryUser(response.user);
      updatePermissions();
    };

    const updatePermissions = () => {
      const userAbilities = formatPermissions(
        user.value?.role?.permissions || [],
        user.value?.role?.is_global_admin || false,
      );
      ability.update(userAbilities);
    };

    const verifyOTP = async (request: VerifyOTPRequestDTO) => {
      const response = await dataSource.verifyOTP(request);

      user.value = response.user;
      token.value = response.token;

      setSentryUser(response.user);
      updatePermissions();
    };

    const forgotPassword = async (email: string) => {
      await dataSource.forgotPassword({ email });
    };

    const resetPassword = async (request: UpdatePasswordRequestDTO) => {
      await dataSource.updatePassword(request);
    };

    const resendOTP = async (email: string) => {
      await dataSource.resendOTP({
        email,
        device_id: getDeviceId(),
      });
    };

    const validateResetToken = async (token: string): Promise<string> => {
      const { email } = await dataSource.validateResetToken(token);
      return email;
    };

    const refreshToken = async () => {
      if (!token.value?.refresh) return;

      const data = await dataSource.refreshToken({
        refresh_token: token.value.refresh,
        device_id: getDeviceId(),
      });

      token.value = data.token;
    };

    const validateTOTP = async (request: ValidateOTPRequestDTO) => {
      const response = await dataSource.validateTOTP(request);

      user.value = response.user;
      token.value = response.token;

      setSentryUser(response.user);
      updatePermissions();
    };

    const validateRecoveryCode = async (
      request: ValidateRecoveryCodeRequestDTO,
    ) => {
      const response = await dataSource.validateRecoveryCode(request);

      user.value = response.user;
      token.value = response.token;

      setSentryUser(response.user);
      updatePermissions();
    };

    const logout = async () => {
      try {
        await dataSource.logout({ device_id: getDeviceId() });
      } finally {
        user.value = null;
        token.value = null;
        setSentryUser(null);
        ability.update([]);
      }
    };

    const switchAccount = async (userId: string) => {
      const response = await dataSource.switchAccount({
        user_id: userId,
        device_id: getDeviceId(),
      });

      user.value = response.user;
      token.value = response.token;
      setSentryUser(response.user);
      updatePermissions();
    };

    const fetchMe = async () => {
      const userData = await dataSource.fetchMe();

      user.value = userData;
      setSentryUser(userData);
      updatePermissions();
    };

    return {
      user,
      fullUserPhoto,
      token,
      userInitials,
      login,
      verifyOTP,
      forgotPassword,
      resetPassword,
      resendOTP,
      validateResetToken,
      refreshToken,
      validateTOTP,
      validateRecoveryCode,
      logout,
      switchAccount,
      updatePermissions,
      fetchMe,
    };
  },
  {
    persist: {
      storage: localStorage,
      pick: ["user", "token"],
    },
  },
);
